import { useState } from 'react';
import { Upload, File, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PdfUploadProps {
  onUploadComplete: (url: string) => void;
  currentUrl?: string;
  label?: string;
  description?: string;
}

export function PdfUpload({ onUploadComplete, currentUrl, label = 'PDF Document', description }: PdfUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Please select a PDF file',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'PDF must be less than 50MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setFileName(file.name);

    try {
      const fileExt = file.name.split('.').pop();
      const randomString = `${Math.random().toString(36).substring(2)}_${Date.now()}`;
      const filePath = `${randomString}.${fileExt}`;

      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('lesson-materials')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get authenticated URL for the uploaded file
      const { data: urlData } = await supabase.storage
        .from('lesson-materials')
        .createSignedUrl(filePath, 365 * 24 * 60 * 60); // 1 year expiry

      const url = urlData?.signedUrl || '';
      
      onUploadComplete(url);
      
      toast({
        title: 'Success',
        description: 'PDF uploaded successfully',
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload PDF',
        variant: 'destructive',
      });
      setFileName(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFileName(null);
    onUploadComplete('');
  };

  // Extract clean filename from URL (remove query params)
  const getCleanFilename = (url: string) => {
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    return lastPart.split('?')[0]; // Remove query parameters
  };
  
  const displayName = fileName || (currentUrl ? getCleanFilename(currentUrl) : null);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">{label}</label>
      </div>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      
      {(displayName || currentUrl) && !uploading ? (
        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
          <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <a 
            href={currentUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm flex-1 truncate hover:underline text-primary"
          >
            {displayName}
          </a>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            className="h-8 w-8 text-destructive hover:bg-destructive/10 flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            disabled={uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="pdf-upload"
          />
          <label
            htmlFor="pdf-upload"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
          >
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload PDF (max 50MB)
                </span>
              </>
            )}
          </label>
        </div>
      )}
    </div>
  );
}
